<template>
    <div class="whatsapp-signup">
        <glass-panel class="whatsapp-signup__intro pa-6 mb-6">
            <div class="whatsapp-signup__intro-copy">
                <div class="whatsapp-signup__icon">
                    <v-icon color="white" size="30">mdi-whatsapp</v-icon>
                </div>
                <div>
                    <div class="eyebrow mb-2">{{ owner.name }}</div>
                    <h2 class="section-title">WhatsApp Business em Coexistence</h2>
                    <p class="body-sm mt-2 mb-0">
                        O número continua funcionando no aplicativo e as novas trocas de mensagens passam a ser registradas para análise.
                    </p>
                </div>
            </div>
            <v-btn
                v-if="canManage"
                color="primary"
                depressed
                :disabled="!configurationReady"
                :loading="launching || submitting"
                @click="launchSignup"
            >
                <v-icon left>mdi-facebook</v-icon>
                {{ integrations.length ? 'Conectar outra conta' : 'Conectar WhatsApp' }}
            </v-btn>
        </glass-panel>

        <v-alert v-if="!configurationReady" type="warning" text class="mb-6">
            A integração da Meta ainda não foi configurada no servidor.
        </v-alert>
        <v-alert v-if="flowError" type="error" text dismissible class="mb-6" @input="flowError = ''">
            {{ flowError }}
        </v-alert>

        <div v-if="integrations.length" class="whatsapp-signup__list">
            <glass-panel
                v-for="integration in integrations"
                :key="integration._id"
                class="whatsapp-account pa-6"
            >
                <div class="whatsapp-account__header">
                    <div>
                        <div class="eyebrow mb-2">Conta do WhatsApp Business</div>
                        <h3>{{ integration.waba_name || 'Conta conectada' }}</h3>
                        <span class="body-xs">WABA {{ integration.waba_id }}</span>
                    </div>
                    <div class="whatsapp-account__status">
                        <v-chip
                            v-if="integration.status === 'connected'"
                            color="primary"
                            small
                            label
                            outlined
                        >
                            <v-icon left size="16">mdi-message-processing-outline</v-icon>
                            {{ ongoingLabel(integration.ongoing_conversations_count) }}
                        </v-chip>
                        <v-chip :color="statusColor(integration.status)" small label outlined>
                            <v-icon left size="16">{{ statusIcon(integration.status) }}</v-icon>
                            {{ statusLabel(integration.status) }}
                        </v-chip>
                    </div>
                </div>

                <v-alert
                    v-if="integration.status === 'disconnect_error'"
                    type="warning"
                    text
                    dense
                    class="mt-5 mb-0"
                >
                    A captura local está interrompida, mas ainda existe uma etapa de desregistro ou remoção da inscrição pendente na Meta.
                </v-alert>

                <div class="whatsapp-account__numbers mt-5">
                    <div
                        v-for="number in integration.numbers"
                        :key="number.ownerPhoneNumberId"
                        class="whatsapp-number"
                    >
                        <v-icon :color="number.captureEnabled ? 'success' : number.lastErrorCode ? 'warning' : 'grey'">
                            mdi-cellphone-message
                        </v-icon>
                        <div class="whatsapp-number__identity">
                            <strong>{{ number.verifiedName || 'WhatsApp Business' }}</strong>
                            <span>{{ number.ownerPhone || number.ownerPhoneNumberId }}</span>
                        </div>
                        <div class="whatsapp-number__controls">
                            <span :class="[
                                'whatsapp-number__state',
                                number.captureEnabled && 'is-active',
                                number.lastErrorCode && 'has-error',
                            ]">
                                {{ numberState(number) }}
                            </span>
                            <v-btn
                                v-if="canManage && (number.captureEnabled || number.lastErrorCode)"
                                text
                                x-small
                                :color="number.lastErrorCode ? 'warning' : 'error'"
                                :loading="disconnectingId === number._id"
                                @click="disconnectNumber(number, Boolean(number.lastErrorCode))"
                            >
                                {{ number.lastErrorCode ? 'Tentar novamente' : 'Desconectar' }}
                            </v-btn>
                        </div>
                    </div>
                </div>

                <div class="whatsapp-account__meta mt-5">
                    <span>Conectado em {{ formatDate(integration.connected_at) }}</span>
                    <span>Último evento: {{ formatDate(integration.last_webhook_at) }}</span>
                </div>
            </glass-panel>
        </div>

        <glass-panel v-else class="pa-8">
            <empty-state
                icon="mdi-whatsapp"
                title="WhatsApp ainda não conectado"
                :description="canManage
                    ? 'Use o botão acima para autorizar o número pelo fluxo oficial da Meta.'
                    : 'Esta clínica ainda não autorizou um número do WhatsApp.'"
            />
        </glass-panel>
    </div>
</template>

<script>
const {
    SIGNUP_EXTRAS,
    loadFacebookSdk,
    normalizedSession,
    parseSessionEvent,
    sessionEventName,
} = require('@/services/metaEmbeddedSignup')

const FINISH_EVENTS = new Set([
    'FINISH',
    'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
])

export default {
    name: 'WhatsAppEmbeddedSignup',
    props: {
        owner: { type: Object, required: true },
        ownerId: { type: String, required: true },
        configuration: { type: Object, required: true },
        configurationReady: { type: Boolean, default: false },
        canManage: { type: Boolean, default: false },
        integrations: { type: Array, default: () => [] },
    },
    data() {
        return {
            launching: false,
            submitting: false,
            disconnectingId: null,
            flowError: '',
            pendingCode: null,
            pendingSession: null,
            attemptCompleted: false,
            waitTimer: null,
        }
    },
    mounted() {
        window.addEventListener('message', this.handleMessage)
    },
    beforeDestroy() {
        window.removeEventListener('message', this.handleMessage)
        this.clearWaitTimer()
    },
    methods: {
        resetAttempt() {
            this.pendingCode = null
            this.pendingSession = null
            this.attemptCompleted = false
            this.flowError = ''
            this.clearWaitTimer()
        },
        clearWaitTimer() {
            if (this.waitTimer) clearTimeout(this.waitTimer)
            this.waitTimer = null
        },
        waitForPair() {
            this.clearWaitTimer()
            this.waitTimer = setTimeout(() => {
                if (!this.attemptCompleted && (!this.pendingCode || !this.pendingSession)) {
                    this.flowError = 'A Meta não retornou todos os dados da conexão. Feche a janela e tente novamente.'
                    this.launching = false
                }
            }, 20000)
        },
        handleMessage(event) {
            const payload = parseSessionEvent(event)
            if (!payload) return
            const eventName = sessionEventName(payload)
            if (FINISH_EVENTS.has(eventName)) {
                const session = normalizedSession(payload)
                if (session.waba_id) {
                    this.pendingSession = session
                    this.waitForPair()
                    void this.completeAttempt()
                }
                return
            }
            if (eventName === 'CANCEL' || eventName.startsWith('ERROR')) {
                this.flowError = eventName === 'CANCEL'
                    ? 'A conexão foi cancelada antes da conclusão.'
                    : 'A Meta não conseguiu concluir a conexão.'
                this.launching = false
                this.clearWaitTimer()
            }
        },
        async launchSignup() {
            if (!this.canManage || !this.configurationReady) return
            this.resetAttempt()
            this.launching = true
            try {
                const FB = await loadFacebookSdk()
                FB.init({
                    appId: this.configuration.app_id,
                    autoLogAppEvents: true,
                    xfbml: true,
                    version: this.configuration.graph_api_version,
                })
                FB.login(response => {
                    if (!response?.authResponse?.code) {
                        this.flowError = 'O login foi fechado ou não concedeu a autorização necessária.'
                        this.launching = false
                        this.clearWaitTimer()
                        return
                    }
                    this.pendingCode = response.authResponse.code
                    this.waitForPair()
                    void this.completeAttempt()
                }, {
                    config_id: this.configuration.config_id,
                    response_type: 'code',
                    override_default_response_type: true,
                    extras: SIGNUP_EXTRAS,
                })
            } catch {
                this.flowError = 'Não foi possível carregar o login da Meta.'
                this.launching = false
            }
        },
        async completeAttempt() {
            if (!this.pendingCode || !this.pendingSession || this.attemptCompleted || this.submitting) return
            this.attemptCompleted = true
            this.submitting = true
            this.clearWaitTimer()
            try {
                const response = await this.$api.connectWhatsAppIntegration({
                    owner_id: this.ownerId,
                    code: this.pendingCode,
                    session: this.pendingSession,
                })
                if (!response?.error) {
                    this.$toast('success', 'WhatsApp conectado e pronto para registrar novas conversas.', 'Conectado')
                    this.$emit('changed')
                } else {
                    this.attemptCompleted = false
                }
            } finally {
                this.submitting = false
                this.launching = false
                this.pendingCode = null
                this.pendingSession = null
            }
        },
        async disconnectNumber(number, retry = false) {
            const phone = number.ownerPhone || number.ownerPhoneNumberId
            const confirmed = retry || await this.$store.dispatch('confirmDialog', {
                title: 'Desconectar este número?',
                message: `Novas mensagens de ${phone} deixarão de ser registradas. Os demais números continuarão ativos e as conversas já capturadas serão preservadas.`,
            })
            if (!confirmed) return
            this.disconnectingId = number._id
            try {
                const response = await this.$api.disconnectWhatsAppIntegration({
                    owner_id: this.ownerId,
                    number_id: number._id,
                })
                if (!response?.error) {
                    const warning = response.message?.warning
                    const successMessage = response.message?.waba_unsubscribed
                        ? 'Número desregistrado e inscrição da conta removida da Meta.'
                        : 'Número desregistrado; os demais continuam ativos.'
                    this.$toast(
                        warning ? 'warning' : 'success',
                        warning || successMessage,
                        warning ? 'Atenção' : 'Desconectado'
                    )
                    this.$emit('changed')
                }
            } finally {
                this.disconnectingId = null
            }
        },
        statusLabel(status) {
            return {
                connecting: 'Conectando',
                connected: 'Conectado',
                disconnecting: 'Desconectando',
                disconnect_error: 'Atenção',
                disconnected: 'Desconectado',
            }[status] || status
        },
        ongoingLabel(value) {
            const count = Number(value) || 0
            return `${count} ${count === 1 ? 'conversa em andamento' : 'conversas em andamento'}`
        },
        numberState(number) {
            if (number.captureEnabled) return 'Capturando'
            if (number.lastErrorCode) return 'Requer nova tentativa'
            return number.deregisteredAt ? 'Desregistrado' : 'Inativo'
        },
        statusColor(status) {
            return status === 'connected' ? 'success' : status === 'disconnect_error' ? 'warning' : 'grey'
        },
        statusIcon(status) {
            return status === 'connected' ? 'mdi-check-circle-outline' : status === 'disconnect_error' ? 'mdi-alert-outline' : 'mdi-circle-outline'
        },
        formatDate(value) {
            return value ? new Date(value).toLocaleString('pt-BR') : '—'
        },
    },
}
</script>

<style scoped>
.whatsapp-signup__intro,
.whatsapp-signup__intro-copy,
.whatsapp-account__header,
.whatsapp-number,
.whatsapp-account__meta,
.whatsapp-account__actions {
    display: flex;
    align-items: center;
}

.whatsapp-signup__intro { justify-content: space-between; gap: 24px; }
.whatsapp-signup__intro-copy { gap: 18px; }
.whatsapp-signup__intro-copy p { max-width: 720px; color: var(--color-on-surface-variant); line-height: 1.6; }
.whatsapp-signup__icon { display: grid; place-items: center; flex: 0 0 58px; height: 58px; border-radius: 18px; background: #16a765; }
.whatsapp-signup__list { display: grid; gap: 18px; }
.whatsapp-account__header { justify-content: space-between; gap: 20px; }
.whatsapp-account__status { display: flex; align-items: center; flex-wrap: wrap; justify-content: flex-end; gap: 8px; }
.whatsapp-account__header h3 { margin: 0 0 4px; font-size: 18px; }
.whatsapp-account__numbers { display: grid; gap: 8px; }
.whatsapp-number { gap: 12px; padding: 12px 14px; border-radius: 14px; background: var(--glass-soft-bg); }
.whatsapp-number__identity { min-width: 0; flex: 1; }
.whatsapp-number strong,
.whatsapp-number span { display: block; }
.whatsapp-number span { color: var(--color-on-surface-variant); font-size: 12px; }
.whatsapp-number__controls { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
.whatsapp-number__state { padding: 4px 9px; border-radius: 20px; background: var(--color-surface-container); font-weight: 700; }
.whatsapp-number__state.is-active { color: var(--color-success); background: var(--color-success-soft); }
.whatsapp-number__state.has-error { color: #e65100; }
.whatsapp-account__meta { flex-wrap: wrap; gap: 8px 20px; color: var(--color-outline); font-size: 11px; }
.whatsapp-account__actions { justify-content: flex-end; }

@media (max-width: 700px) {
    .whatsapp-signup__intro,
    .whatsapp-account__header { align-items: flex-start; flex-direction: column; }
    .whatsapp-signup__intro > .v-btn { width: 100%; }
    .whatsapp-number { align-items: flex-start; flex-wrap: wrap; }
    .whatsapp-number__controls { width: 100%; padding-left: 36px; justify-content: space-between; }
}
</style>
